import React from 'react'
import moment from 'moment-timezone'
import { startCase } from 'lodash'
import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  ListItem,
  SimpleGrid,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  UnorderedList
} from '@chakra-ui/react'

import type {
  LeaseCreationDraftResponse,
  LeaseCreationFormValues
} from '@homevest/types/rental-applications/lease-creation'

export type EditableLeaseValues = Record<string, any>

type LeaseInputProps = {
  error?: string
  helperText?: string
  inputMode?: 'decimal' | 'numeric'
  isDisabled?: boolean
  isRequired?: boolean
  label: string
  max?: number | string
  min?: number | string
  onChange: (value: string) => void
  placeholder?: string
  prefix?: string
  step?: string
  suffix?: string
  type?: 'date' | 'number' | 'text' | 'month'
  value: unknown
}

const LeaseInput = ({
  error,
  helperText,
  inputMode,
  isDisabled,
  isRequired,
  label,
  max,
  min,
  onChange,
  placeholder,
  prefix,
  step,
  suffix,
  type = 'text',
  value
}: LeaseInputProps) => {
  const showDisabledHelperTooltip = Boolean(isDisabled && helperText)
  const inputGroup = (
    <InputGroup pointerEvents={showDisabledHelperTooltip ? 'none' : undefined}>
      {prefix && (
        <InputLeftElement pointerEvents='none' color='gray.500'>
          {prefix}
        </InputLeftElement>
      )}
      <Input
        inputMode={inputMode}
        isDisabled={isDisabled}
        max={max}
        min={min}
        placeholder={placeholder}
        step={step}
        type={type}
        value={value === null || value === undefined ? '' : String(value)}
        onChange={(event) => onChange(event.target.value)}
      />
      {suffix && (
        <InputRightElement pointerEvents='none' color='gray.500'>
          {suffix}
        </InputRightElement>
      )}
    </InputGroup>
  )

  return (
    <FormControl isInvalid={Boolean(error)} isRequired={isRequired}>
      <FormLabel>{label}</FormLabel>
      {showDisabledHelperTooltip ? (
        <Tooltip
          hasArrow
          label={helperText}
          maxW='280px'
          openDelay={200}
          placement='top-start'
          closeOnScroll
        >
          <Box aria-label={`${label}. ${helperText}`} cursor='help' role='group' tabIndex={0}>
            {inputGroup}
          </Box>
        </Tooltip>
      ) : (
        inputGroup
      )}
      {helperText && !error && !showDisabledHelperTooltip && (
        <FormHelperText>{helperText}</FormHelperText>
      )}
      {error && <FormErrorMessage>{error}</FormErrorMessage>}
    </FormControl>
  )
}

const numberValue = (value: string) => (value === '' ? '' : Number(value))

const MONEY_INPUT_PATTERN = /^\$?(?:(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d{0,2})?|\.\d{1,2})$/

const numberFromMoney = (value: unknown): number => {
  const text = String(value ?? '').trim()
  const number = Number(text.replace(/[$,]/g, ''))
  return MONEY_INPUT_PATTERN.test(text) &&
    Number.isFinite(number) &&
    number >= 0 &&
    number <= 99_999_999.99
    ? number
    : 0
}

const displayMoney = (value: unknown) => {
  if (value === '' || value === null || value === undefined) {
    return 'Not entered'
  }
  const number = numberFromMoney(value)
  return number.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

const rentalProgramName = (kind: string): string =>
  kind === 'standard' ? 'Up&Up lease' : 'Vanilla lease'

const LeasePanel = ({
  children,
  description,
  title
}: React.PropsWithChildren<{ description?: string; title: string }>) => (
  <Box bg='white' borderColor='gray.200' borderRadius='lg' borderWidth='1px' p={{ base: 4, md: 5 }}>
    <Heading size='md'>{title}</Heading>
    {description && (
      <Text color='gray.600' fontSize='sm' mt={1} mb={4}>
        {description}
      </Text>
    )}
    {!description && <Box mb={4} />}
    {children}
  </Box>
)

export const LeaseDraftForm = ({
  draft,
  fieldErrors,
  isLoading,
  onChange,
  onReview,
  values
}: {
  draft: LeaseCreationDraftResponse
  fieldErrors: Record<string, string>
  isLoading: boolean
  onChange: (values: EditableLeaseValues) => void
  onReview: () => void
  values: EditableLeaseValues
}) => {
  const setValue = (key: string, value: unknown) => {
    onChange({ ...values, [key]: value })
  }
  const input = (
    field: string,
    label: string,
    {
      displayValue,
      numeric = false,
      ...inputProps
    }: Pick<
      LeaseInputProps,
      | 'helperText'
      | 'inputMode'
      | 'isDisabled'
      | 'isRequired'
      | 'max'
      | 'min'
      | 'placeholder'
      | 'prefix'
      | 'step'
      | 'suffix'
      | 'type'
    > & {
      displayValue?: unknown
      numeric?: boolean
    } = {}
  ) => (
    <LeaseInput
      {...inputProps}
      label={label}
      value={displayValue === undefined ? values[field] : displayValue}
      error={fieldErrors[field]}
      onChange={(value) => setValue(field, numeric ? numberValue(value) : value)}
    />
  )
  const moneyInput = (field: string, label: string, options: Parameters<typeof input>[2] = {}) =>
    input(field, label, {
      inputMode: 'decimal',
      placeholder: '0.00',
      prefix: '$',
      ...options
    })
  const concessions = Array.isArray(values.concessions) ? values.concessions : []
  const setConcessions = (nextConcessions: Array<Record<string, unknown>>) => {
    const total = nextConcessions.reduce(
      (sum, concession) => sum + numberFromMoney(concession.amount),
      0
    )
    onChange({
      ...values,
      concessions: nextConcessions,
      concessionTotal: total.toFixed(2)
    })
  }
  const programName = rentalProgramName(values.kind)

  return (
    <Stack spacing={6}>
      <Box bg='gray.50' borderColor='gray.200' borderRadius='lg' borderWidth='1px' p={5}>
        <Flex align={{ base: 'start', md: 'center' }} justify='space-between' gap={4} wrap='wrap'>
          <Box>
            <Flex align='center' gap={3} wrap='wrap'>
              <Text fontSize='lg' fontWeight='semibold'>
                {draft.property.address}
              </Text>
              <Badge colorScheme={values.kind === 'standard' ? 'blue' : 'yellow'}>
                {programName}
              </Badge>
              {values.kind === 'kcpoa' && <Badge colorScheme='teal'>Rental purchase program</Badge>}
              {values.kind === 'sandalwood' && values.guarantor && (
                <Badge colorScheme='purple'>The Guarantors</Badge>
              )}
            </Flex>
            <Text color='gray.600' fontSize='sm' mt={2}>
              Template: {draft.template.description}
            </Text>
          </Box>
          <Box textAlign={{ base: 'left', md: 'right' }}>
            <Text color='gray.600' fontSize='sm'>
              Monthly rent
            </Text>
            <Text fontSize='xl' fontWeight='semibold'>
              {displayMoney(draft.serverOwned.rent)}
            </Text>
          </Box>
        </Flex>
      </Box>

      <LeasePanel title='Lease terms'>
        <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
          {input('initialLeaseTermMonths', 'Initial lease term (months)', {
            type: 'number',
            min: values.kind === 'kcpoa' ? 3 : 1,
            max: 60,
            numeric: true
          })}
          {input('occupancyDate', 'Occupancy date', {
            type: 'date',
            min: moment().tz('America/New_York').format('YYYY-MM-DD')
          })}
          {input('stepUpPercentagePoints', 'Step-up percentage', {
            type: 'number',
            step: '0.01',
            min: 0,
            max: 30,
            suffix: '%',
            numeric: true
          })}
        </SimpleGrid>
      </LeasePanel>

      <LeasePanel title='Lease fees'>
        <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
          {values.kind === 'standard'
            ? moneyInput('initialOptionPremium', 'Initial option premium (upfront)')
            : values.kind === 'sandalwood'
            ? moneyInput('securityDepositAmount', 'Security deposit', {
                displayValue: values.guarantor ? '0.00' : undefined,
                isDisabled: values.guarantor,
                helperText: values.guarantor
                  ? 'Set to $0 because The Guarantors bond applies.'
                  : undefined
              })
            : null}
          {values.kind === 'standard' && draft.serverOwned.monthlyOption !== undefined
            ? moneyInput('monthlyOption', 'Monthly option premium', {
                displayValue: draft.serverOwned.monthlyOption,
                isDisabled: true,
                helperText: 'Set by the lease program.'
              })
            : null}
          {moneyInput('utilityFee', 'Utility fee (monthly)')}
          {moneyInput('internetFee', 'Internet fee (monthly)', {
            displayValue: draft.serverOwned.internetFee,
            isDisabled: true,
            helperText: 'Set automatically for this property.'
          })}
          {moneyInput('petRent', 'Pet rent (monthly)')}
          {moneyInput('upfrontPetFees', 'Pet fees (upfront)')}
          {moneyInput('amenityFee', 'Amenity fee (monthly)')}
          {moneyInput('otherMonthly', 'Other fees (monthly)')}
        </SimpleGrid>
      </LeasePanel>

      {values.kind === 'kcpoa' && (
        <LeasePanel
          title='Purchase program terms'
          description='Confirm the purchase option amounts for this lease.'
        >
          <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
            {moneyInput('initialOptionFee', 'Initial option fee (upfront)')}
            {moneyInput('monthlyOptionFee', 'Monthly option fee')}
            {moneyInput('purchasePrice', 'Purchase price', {
              isRequired: true
            })}
          </SimpleGrid>
        </LeasePanel>
      )}

      <LeasePanel
        title='Concessions'
        description='Add only the months where the resident receives a rent credit.'
      >
        <Flex align='center' justify='space-between' mb={3}>
          <Text color='gray.600' fontSize='sm'>
            Total:{' '}
            <Text as='span' fontWeight='semibold'>
              {displayMoney(values.concessionTotal)}
            </Text>
          </Text>
          <Button
            size='sm'
            variant='outline'
            isDisabled={concessions.length >= 13}
            onClick={() => setConcessions([...concessions, { amount: '0.00', month: '' }])}
          >
            Add concession
          </Button>
        </Flex>
        {fieldErrors.concessionTotal && (
          <Text color='red.500' fontSize='sm' mb={3}>
            {fieldErrors.concessionTotal}
          </Text>
        )}
        {concessions.length === 0 ? (
          <Box bg='gray.50' borderRadius='md' px={4} py={3}>
            <Text color='gray.600' fontSize='sm'>
              No concessions added.
            </Text>
          </Box>
        ) : (
          <Stack spacing={4}>
            {input('concessionDescription', 'Concession description', {
              isRequired: numberFromMoney(values.concessionTotal) > 0
            })}
            {concessions.map((concession: Record<string, unknown>, index: number) => (
              <Flex
                key={index}
                direction={{ base: 'column', md: 'row' }}
                gap={3}
                align={{ base: 'stretch', md: 'end' }}
              >
                <Box flex={1}>
                  <LeaseInput
                    inputMode='decimal'
                    label={`Concession ${index + 1} amount`}
                    placeholder='0.00'
                    prefix='$'
                    value={concession.amount}
                    error={fieldErrors[`concessions.${index}.amount`]}
                    onChange={(amount) => {
                      const next = [...concessions]
                      next[index] = { ...next[index], amount }
                      setConcessions(next)
                    }}
                  />
                </Box>
                <Box flex={1}>
                  <LeaseInput
                    label={`Concession ${index + 1} month`}
                    type='month'
                    value={concession.month}
                    error={fieldErrors[`concessions.${index}.month`]}
                    onChange={(month) => {
                      const next = [...concessions]
                      next[index] = { ...next[index], month }
                      setConcessions(next)
                    }}
                  />
                </Box>
                <Button
                  colorScheme='red'
                  variant='ghost'
                  onClick={() =>
                    setConcessions(
                      concessions.filter((_: unknown, concessionIndex: number) => {
                        return concessionIndex !== index
                      })
                    )
                  }
                >
                  Remove
                </Button>
              </Flex>
            ))}
          </Stack>
        )}
      </LeasePanel>

      {fieldErrors._request && (
        <Alert status='error'>
          <AlertIcon />
          {fieldErrors._request}
        </Alert>
      )}

      <Flex justify='flex-end'>
        <Button
          colorScheme='teal'
          minW={{ base: 'full', sm: '220px' }}
          size='lg'
          onClick={onReview}
          isLoading={isLoading}
        >
          Review lease
        </Button>
      </Flex>
    </Stack>
  )
}

const Participants = ({
  participants
}: {
  participants: LeaseCreationDraftResponse['participants']
}) => (
  <LeasePanel
    title='Tenants and required lease details'
    description='To make changes, return to the application and edit Required Lease Details.'
  >
    {participants.tenants.length ? (
      <TableContainer borderWidth='1px' borderRadius='md'>
        <Table size='sm'>
          <Thead>
            <Tr>
              <Th>Role</Th>
              <Th>Name</Th>
              <Th>Email</Th>
              <Th>Phone</Th>
            </Tr>
          </Thead>
          <Tbody>
            {participants.tenants.map((tenant) => (
              <Tr key={`${tenant.email}:${tenant.role}`}>
                <Td>{startCase(tenant.role)}</Td>
                <Td>{tenant.name || '—'}</Td>
                <Td>{tenant.email || '—'}</Td>
                <Td>{tenant.phone || '—'}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    ) : (
      <Alert status='error'>
        <AlertIcon />
        No lease signers were found. Return to the application before creating the lease.
      </Alert>
    )}
    <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={4} mt={4}>
      <ParticipantList
        title='Occupants'
        rows={participants.occupants.map((occupant) => {
          return `${occupant.name}, age ${occupant.age} (${occupant.relationship})`
        })}
      />
      <ParticipantList
        title='Vehicles'
        rows={participants.vehicles.map((vehicle) => {
          return `${vehicle.year} ${vehicle.make} ${vehicle.model} — ${vehicle.licensePlate}`
        })}
      />
      <ParticipantList
        title='Pets'
        rows={participants.pets.map((pet) => {
          return `${pet.name}, ${pet.type}, ${pet.color}, ${pet.weightInPounds} lb`
        })}
      />
    </SimpleGrid>
  </LeasePanel>
)

const ParticipantList = ({ rows, title }: { rows: string[]; title: string }) => (
  <Box borderWidth='1px' borderRadius='md' p={3}>
    <Text fontWeight='semibold'>{title}</Text>
    {rows.length ? (
      <UnorderedList mt={2}>
        {rows.map((row) => (
          <ListItem key={row}>{row}</ListItem>
        ))}
      </UnorderedList>
    ) : (
      <Text mt={2} color='gray.500'>
        None
      </Text>
    )}
  </Box>
)

const ReviewDetails = ({ rows }: { rows: Array<{ label: string; value: React.ReactNode }> }) => (
  <LeasePanel title='Lease details'>
    <TableContainer borderWidth='1px' borderRadius='md'>
      <Table size='sm'>
        <Thead>
          <Tr>
            <Th>Detail</Th>
            <Th>Value</Th>
          </Tr>
        </Thead>
        <Tbody>
          {rows.map((row) => (
            <Tr key={row.label}>
              <Td>{row.label}</Td>
              <Td>{row.value}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  </LeasePanel>
)

export const LeaseReview = ({
  draft,
  error,
  isLoading,
  isSubmitDisabled,
  onBack,
  onSubmit,
  values
}: {
  draft: LeaseCreationDraftResponse
  error?: string
  isLoading: boolean
  isSubmitDisabled: boolean
  onBack: () => void
  onSubmit: () => void
  values: LeaseCreationFormValues
}) => {
  const activeConcessions = values.concessions.filter((concession) => Number(concession.amount) > 0)
  const rows: Array<{ label: string; value: React.ReactNode }> = [
    { label: 'Property', value: draft.property.address },
    { label: 'Template', value: draft.template.description },
    { label: 'Rental program', value: rentalProgramName(values.kind) },
    { label: 'Initial lease term', value: `${values.initialLeaseTermMonths} months` },
    {
      label: 'Occupancy date',
      value: moment(values.occupancyDate, 'YYYY-MM-DD', true).format('MMMM D, YYYY')
    },
    { label: 'Monthly rent', value: displayMoney(draft.serverOwned.rent) },
    { label: 'Monthly utility fee', value: displayMoney(values.utilityFee) },
    { label: 'Monthly internet fee', value: displayMoney(draft.serverOwned.internetFee) },
    { label: 'Monthly pet rent', value: displayMoney(values.petRent) },
    { label: 'Upfront pet fees', value: displayMoney(values.upfrontPetFees) },
    { label: 'Step-up percentage', value: `${values.stepUpPercentagePoints}%` }
  ]

  if (values.kind === 'standard') {
    rows.push(
      { label: 'Initial option premium', value: displayMoney(values.initialOptionPremium) },
      { label: 'Monthly option premium', value: displayMoney(draft.serverOwned.monthlyOption) },
      { label: 'Monthly amenity fee', value: displayMoney(values.amenityFee) },
      { label: 'Other monthly fees', value: displayMoney(values.otherMonthly) }
    )
  } else if (values.kind === 'sandalwood') {
    rows.push(
      {
        label: 'Security deposit',
        value: displayMoney(values.guarantor ? '0.00' : values.securityDepositAmount)
      },
      { label: 'Monthly amenity fee', value: displayMoney(values.amenityFee) },
      { label: 'Other monthly fees', value: displayMoney(values.otherMonthly) }
    )
  } else {
    rows.push(
      { label: 'Monthly amenity fee', value: displayMoney(values.amenityFee) },
      { label: 'Initial option fee', value: displayMoney(values.initialOptionFee) },
      { label: 'Monthly option fee', value: displayMoney(values.monthlyOptionFee) },
      { label: 'Other monthly fees', value: displayMoney(values.otherMonthly) },
      { label: 'Purchase price', value: displayMoney(values.purchasePrice) }
    )
  }

  rows.push({ label: 'Total concessions', value: displayMoney(values.concessionTotal) })
  if (activeConcessions.length > 0) {
    rows.push({
      label: 'Concession description',
      value: values.concessionDescription || 'None'
    })
  }

  return (
    <Stack spacing={6}>
      <Alert status='warning'>
        <AlertIcon />
        <Box>
          <AlertTitle>Review before creating the lease</AlertTitle>
          <AlertDescription>
            Creating and sending the lease starts a signature request and cannot be undone.
          </AlertDescription>
        </Box>
      </Alert>

      <ReviewDetails rows={rows} />

      {activeConcessions.length > 0 && (
        <ParticipantList
          title='Concessions breakdown'
          rows={activeConcessions.map((concession) => {
            return `${moment(concession.month, 'YYYY-MM', true).format('MMM YYYY')}: ${displayMoney(
              concession.amount
            )}`
          })}
        />
      )}

      <Participants participants={draft.participants} />

      {error && (
        <Alert status='error'>
          <AlertIcon />
          <Box>
            <AlertTitle>Lease creation error</AlertTitle>
            <AlertDescription>
              <Text>{error}</Text>
              <Text mt={1}>
                Sending is disabled to avoid a duplicate request. Return to the application and
                confirm whether a lease was created.
              </Text>
            </AlertDescription>
          </Box>
        </Alert>
      )}

      <Flex direction={{ base: 'column', sm: 'row' }} justify='flex-end' gap={3}>
        <Button
          size='lg'
          width={{ base: 'full', sm: 'auto' }}
          minW={{ sm: '160px' }}
          variant='outline'
          leftIcon={<ArrowLeftIcon className='h-4 w-4' />}
          onClick={onBack}
          isDisabled={isLoading}
        >
          Edit lease
        </Button>
        <Button
          colorScheme='teal'
          minW={{ sm: '230px' }}
          size='lg'
          width={{ base: 'full', sm: 'auto' }}
          onClick={onSubmit}
          isDisabled={isSubmitDisabled}
          isLoading={isLoading}
        >
          Create and send lease
        </Button>
      </Flex>
    </Stack>
  )
}
